import "./style.css";
import type { ActionName, RemoteEvent } from "./protocol";
import { sendRemoteEvent, setRemoteEventSender } from "./protocol";
import { connectControlWs, joinRoom } from "./cloudflare";

function getQueryParam(name: string): string | null {
  const params = new URLSearchParams(window.location.search);
  return params.get(name);
}

function clamp01(n: number): number {
  return Math.min(1, Math.max(0, n));
}

function escapeHtml(s: string): string {
  return s
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

function mountJoin(): void {
  const app = document.getElementById("app");
  if (!app) return;

  const roomFromUrl = getQueryParam("room") ?? "";

  app.innerHTML = `
    <div class="screen">
      <div class="join-panel">
        <h1>远程控制</h1>
        <div class="field">
          <label for="room">Room ID</label>
          <input id="room" autocomplete="off" autocapitalize="off" spellcheck="false" value="${escapeHtml(roomFromUrl)}" />
        </div>
        <div class="field">
          <label for="password">连接密码</label>
          <input id="password" type="password" autocomplete="current-password" />
        </div>
        <button class="btn-primary" id="join" type="button">连接</button>
        <p class="hint">
          首次使用需在被控安卓端开启无障碍并授权 RemoteHost。<br />
          当前使用 Cloudflare Worker 校验密码并通过 WebSocket 建立控制通道。
        </p>
      </div>
    </div>
  `;

  const roomInput = document.getElementById("room") as HTMLInputElement;
  const passwordInput = document.getElementById("password") as HTMLInputElement;
  const joinBtn = document.getElementById("join") as HTMLButtonElement;

  joinBtn.addEventListener("click", () => {
    const room = roomInput.value.trim();
    const password = passwordInput.value;
    if (!room) {
      alert("请输入 Room ID");
      return;
    }
    if (!password) {
      alert("请输入连接密码");
      return;
    }
    void joinRemote(room, password);
  });
}

async function joinRemote(room: string, _password: string): Promise<void> {
  try {
    const token = await joinRoom(room, _password);
    const sender = connectControlWs(room, token);
    setRemoteEventSender(sender);

    mountControl();
  } catch (error) {
    alert(`连接失败: ${error instanceof Error ? error.message : String(error)}`);
    console.error('Join room failed:', error);
  }
}

function mountControl(): void {
  const app = document.getElementById("app");
  if (!app) return;

  app.innerHTML = `
    <div class="control-root">
      <div class="remote-video-wrap">
        <video id="remoteVideo" autoplay playsinline muted class="hidden"></video>
        <div class="remote-placeholder" id="remotePlaceholder">
          尚未建立 WebRTC 视频流（下一步接入 Firebase 信令 + 安卓 ScreenCapture）。<br /><br />
          下方可试用单手控制与按钮事件（控制台会打印协议 JSON）。
        </div>
      </div>
      <div class="touch-layer" id="touchLayer"></div>
      <div class="assist-dot" id="assistDot" style="left: 16px; bottom: calc(24px + env(safe-area-inset-bottom)); top: auto;"></div>
      <div class="toolbar" id="toolbar">
        <button type="button" data-action="back">返回</button>
        <button type="button" data-action="home">主页</button>
        <button type="button" data-action="recents">最近</button>
        <button type="button" data-action="screen_lock">锁屏</button>
        <button type="button" data-action="wake">唤醒</button>
      </div>
      <div class="input-bar">
        <input id="textInput" type="text" placeholder="远控输入文本（发送到安卓）" autocomplete="off" />
      </div>
    </div>
  `;

  const touchLayer = document.getElementById("touchLayer") as HTMLDivElement;
  const assistDot = document.getElementById("assistDot") as HTMLDivElement;
  const toolbar = document.getElementById("toolbar") as HTMLDivElement;
  const textInput = document.getElementById("textInput") as HTMLInputElement;

  const isAction = (v: string | undefined): v is ActionName =>
    v === "back" ||
    v === "home" ||
    v === "recents" ||
    v === "screen_lock" ||
    v === "wake";

  toolbar.addEventListener("click", (e) => {
    const btn = (e.target as HTMLElement).closest("button");
    const a = btn?.dataset.action;
    if (!isAction(a)) return;
    const ev: RemoteEvent = { t: "action", name: a };
    sendRemoteEvent(ev);
    // eslint-disable-next-line no-console
    console.log("[remote]", JSON.stringify(ev));
  });

  touchLayer.addEventListener(
    "pointerdown",
    (e) => {
      if (e.target !== touchLayer) return;
      const rect = touchLayer.getBoundingClientRect();
      const x = clamp01((e.clientX - rect.left) / rect.width);
      const y = clamp01((e.clientY - rect.top) / rect.height);
      const ev: RemoteEvent = { t: "tap", x, y };
      sendRemoteEvent(ev);
      // eslint-disable-next-line no-console
      console.log("[remote]", JSON.stringify(ev));
    },
    { passive: true }
  );

  setupAssistDot(assistDot, touchLayer);

  textInput.addEventListener("input", () => {
    const ev: RemoteEvent = { t: "text", text: textInput.value, mode: "replace" };
    sendRemoteEvent(ev);
    // eslint-disable-next-line no-console
    console.log("[remote]", JSON.stringify(ev));
  });
}

function dotCenterNorm(
  dot: HTMLElement,
  touchLayer: HTMLElement
): { x: number; y: number } {
  const rect = touchLayer.getBoundingClientRect();
  const cx = dot.getBoundingClientRect();
  const centerX = (cx.left + cx.right) / 2;
  const centerY = (cx.top + cx.bottom) / 2;
  return {
    x: clamp01((centerX - rect.left) / rect.width),
    y: clamp01((centerY - rect.top) / rect.height),
  };
}

function setupAssistDot(dot: HTMLDivElement, touchLayer: HTMLDivElement): void {
  let dragging = false;
  let startPx = 0;
  let startPy = 0;
  let originLeft = 0;
  let originBottom = 0;
  let startTime = 0;
  let moved = false;
  let startNorm = { x: 0, y: 0 };

  const parent = dot.parentElement as HTMLElement;

  const setPos = (left: number, bottom: number) => {
    dot.style.left = `${left}px`;
    dot.style.bottom = `${bottom}px`;
    dot.style.top = "auto";
  };

  dot.addEventListener("pointerdown", (e) => {
    e.preventDefault();
    dragging = true;
    moved = false;
    startTime = performance.now();
    try {
      dot.setPointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
    const rect = dot.getBoundingClientRect();
    const parentRect = parent.getBoundingClientRect();
    startPx = e.clientX;
    startPy = e.clientY;
    originLeft = rect.left - parentRect.left;
    originBottom = parentRect.bottom - rect.bottom;
    startNorm = dotCenterNorm(dot, touchLayer);
  });

  dot.addEventListener("pointermove", (e) => {
    if (!dragging) return;
    const dx = e.clientX - startPx;
    const dy = e.clientY - startPy;
    if (Math.hypot(dx, dy) > 6) moved = true;
    const parentRect = parent.getBoundingClientRect();
    const newLeft = originLeft + dx;
    const newBottom = originBottom - dy;
    const maxLeft = parentRect.width - dot.offsetWidth - 8;
    const maxBottom = parentRect.height - dot.offsetHeight - 8;
    setPos(
      Math.min(Math.max(8, newLeft), maxLeft),
      Math.min(Math.max(8, newBottom), maxBottom)
    );
  });

  const endGesture = (e: PointerEvent) => {
    if (!dragging) return;
    dragging = false;
    try {
      dot.releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }

    const dur = performance.now() - startTime;
    const endNorm = dotCenterNorm(dot, touchLayer);

    if (!moved) {
      const ev: RemoteEvent =
        dur > 550
          ? { t: "longpress", x: endNorm.x, y: endNorm.y, durMs: Math.round(dur) }
          : { t: "tap", x: endNorm.x, y: endNorm.y };
      sendRemoteEvent(ev);
      // eslint-disable-next-line no-console
      console.log("[remote]", JSON.stringify(ev));
      return;
    }

    const ev: RemoteEvent = {
      t: "swipe",
      sx: startNorm.x,
      sy: startNorm.y,
      ex: endNorm.x,
      ey: endNorm.y,
      durMs: Math.min(1200, Math.max(120, Math.round(dur))),
    };
    sendRemoteEvent(ev);
    // eslint-disable-next-line no-console
    console.log("[remote]", JSON.stringify(ev));
  };

  dot.addEventListener("pointerup", endGesture);
  dot.addEventListener("pointercancel", endGesture);
}

mountJoin();
